import React, { PropsWithChildren } from "react";
import { css, keyframes } from "@emotion/react";
import { ModalProps } from "./types";
import Cross from "icons/Cross";
import { Spacing } from "atoms/types";
import Animated from "molecules/Animated";

// Define a slide-up animation that fades in the modal.
const slideUp = keyframes`
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

// Define modal sizes
const modalSizes = {
	lg: "968px",
	md: "800px",
	sm: "600px",
	xs: "400px",
	xl: "calc(100% - 120px)"
};

const modalStyles = {
	overlay: css`
		position: fixed;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		display: flex;
		justify-content: center;
		align-items: center;
		z-index: 1000;
		background-color: rgba(0, 0, 0, 0.78);
	`,
	content: (size: Spacing) => css`
		background: var(--color-dark-gray);
		padding-top: var(--spacing-xxxl); // handling for Some Browser !!
		padding-bottom: var(--spacing-xxl);
		padding-left: var(--spacing-lg);
		padding-right: var(--spacing-lg);
		border-radius: var(--spacing-xxl);
		box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
		position: relative;
		width: ${modalSizes[size]};
		@media (max-width: 1200px) {
			width: 100vw;
			height: 100dvh;
			border-radius: 0px;
		}
	`,
	header: css`
		padding-bottom: var(--spacing-sm);
		margin-bottom: var(--spacing-xl);
	`,
	body: css`
		max-height: 70vh; /* Adjust the height as needed */
		overflow-y: auto;
		@media (max-width: 1200px) {
			max-height: 100%;
			height: 100%;
		}
	`,
	footer: css`
		text-align: right;
		padding-top: var(--spacing-xl);
		background-color: var(--color-dark-gray);
		@media (max-width: 1200px) {
			position: fixed;
			bottom: 0px;
			width: 100%;
			left: 0px;
			padding: var(--spacing-md);
		}
	`,
	closeButton: css`
		position: absolute;
		top: 12px;
		right: 12px;
		cursor: pointer;
	`
};

const Modal: React.FC<PropsWithChildren<ModalProps>> & {
	Header: React.FC<PropsWithChildren<{}>>;
	Body: React.FC<PropsWithChildren<{}>>;
	Footer: React.FC<PropsWithChildren<{}>>;
} = ({ isOpen, onClose, isCloseEnabled = true, size = "md", children }) => {

	React.useEffect(() => {
		if (isOpen) {

			// Store the current overflow value so we can restore it later
			const originalOverflow = document.body.style.overflow;
			// const orginalZoom = document.body.style.zoom
			document.body.style.overflow = "hidden";

			// Cleanup function to restore the original overflow when the modal unmounts
			return () => {
				document.body.style.overflow = originalOverflow;
			};
		}
		return () => { };
	}, [isOpen]);

	if (!isOpen) return null;

	return (
		<div css={modalStyles.overlay}>
			<Animated
				trigger="inview"
				animation="fadeInUp"
				duration={0.5}
				timingFunction="ease-in-out"
				iteration={1}
			>
				<div css={modalStyles.content(size)}>
					{isCloseEnabled ? (
						<div css={modalStyles.closeButton} onClick={onClose}>
							<Cross />
						</div>
					) : <></>}
					{children}
				</div>
			</Animated>
		</div>
	);
};

Modal.Header = ({ children }) => <div css={modalStyles.header}>{children}</div>;
Modal.Body = ({ children }) => <div css={modalStyles.body}>{children}</div>;
Modal.Footer = ({ children }) => <div css={modalStyles.footer}>{children}</div>;

export default Modal;
